// messaging-tools.js — fonctions pures v4.6.0.
// Recherche, préférences locales de conversations, galerie et messages
// éphémères. Rien ici ne communique avec le serveur.

export const EPHEMERAL_OPTIONS = Object.freeze([
  { seconds: 0, label: 'Conserver' },
  { seconds: 3600, label: '1 heure' },
  { seconds: 86400, label: '1 jour' },
  { seconds: 604800, label: '7 jours' },
]);

const ALLOWED_EPHEMERAL = new Set(EPHEMERAL_OPTIONS.map((x) => x.seconds));

export function normalizeEphemeralSeconds(value) {
  const n = Math.floor(Number(value) || 0);
  return ALLOWED_EPHEMERAL.has(n) ? n : 0;
}

export function computeExpiresAt({ ephemeralSeconds = 0, deliverAt = 0, now = Date.now() } = {}) {
  const seconds = normalizeEphemeralSeconds(ephemeralSeconds);
  if (!seconds) return 0;
  const scheduled = Number(deliverAt || 0);
  const base = Number.isFinite(scheduled) && scheduled > now ? scheduled : now;
  return base + seconds * 1000;
}

export function isExpiredMessage(message, now = Date.now()) {
  const expiresAt = Number(message?.expiresAt || 0);
  return Number.isFinite(expiresAt) && expiresAt > 0 && expiresAt <= now;
}


export function formatElapsed(from, now = Date.now()) {
  const start = Number(from || 0);
  const end = Number(now || Date.now());
  if (!start || !Number.isFinite(start) || !Number.isFinite(end)) return '';
  const seconds = Math.max(0, Math.floor((end - start) / 1000));
  const hh = Math.floor(seconds / 3600);
  const mm = String(Math.floor((seconds % 3600) / 60)).padStart(2, '0');
  const ss = String(seconds % 60).padStart(2, '0');
  return hh ? `${hh}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function normalizeConversationKeys(value) {
  const out = [];
  const seen = new Set();
  for (const raw of Array.isArray(value) ? value : []) {
    const key = typeof raw === 'string' ? raw.trim() : '';
    if (!key || seen.has(key)) continue;
    seen.add(key); out.push(key);
  }
  return out.slice(0, 500);
}

function textOfMessage(m) {
  const parts = [m?.text, m?.fileName, m?.from, m?.peer, m?.poll?.question];
  if (Array.isArray(m?.poll?.options)) parts.push(...m.poll.options);
  if (Array.isArray(m?.shoppingList?.items)) parts.push(...m.shoppingList.items);
  return parts.filter((x) => typeof x === 'string' && x).join(' ').normalize('NFKD').toLocaleLowerCase('fr');
}

function excerptOf(m) {
  const source = typeof m?.text === 'string' && m.text.trim()
    ? m.text.trim()
    : typeof m?.fileName === 'string' && m.fileName.trim()
      ? `📎 ${m.fileName.trim()}`
      : m?.poll?.question
        ? `📊 ${m.poll.question}`
        : m?.shoppingList?.items?.length
          ? `🛒 ${m.shoppingList.items.slice(0, 3).join(', ')}`
          : m?.msgType === 'audio' ? '🎤 Message vocal'
            : m?.msgType === 'image' ? '🖼 Image'
              : m?.msgType === 'video' ? '🎬 Vidéo' : 'Message';
  return source.length > 120 ? `${source.slice(0, 120)}…` : source;
}

export function searchLocalMessages(messages, query, { now = Date.now(), limit = 60 } = {}) {
  const q = String(query || '').trim().normalize('NFKD').toLocaleLowerCase('fr');
  if (q.length < 2) return [];
  const terms = q.split(/\s+/).filter(Boolean).slice(0, 8);
  return (Array.isArray(messages) ? messages : [])
    .filter((m) => m && !isExpiredMessage(m, now))
    .map((m) => ({ m, haystack: textOfMessage(m) }))
    .filter(({ haystack }) => terms.every((term) => haystack.includes(term)))
    .sort((a, b) => Number(b.m.sentAt || 0) - Number(a.m.sentAt || 0))
    .slice(0, Math.max(1, Math.min(200, Number(limit) || 60)))
    .map(({ m }) => ({
      localId: m.localId,
      peer: String(m.peer || ''),
      from: String(m.from || ''),
      sentAt: Number(m.sentAt || 0),
      excerpt: excerptOf(m),
      msgType: String(m.msgType || 'text'),
    }));
}

export function mediaForConversation(messages, { now = Date.now(), limit = 80 } = {}) {
  const types = new Set(['image', 'video', 'audio', 'document', 'archive']);
  return (Array.isArray(messages) ? messages : [])
    .filter((m) => m && types.has(m.msgType) && !m.viewOnce && (m.data || m.blob) && !isExpiredMessage(m, now))
    .sort((a, b) => Number(b.sentAt || 0) - Number(a.sentAt || 0))
    .slice(0, Math.max(1, Math.min(200, Number(limit) || 80)));
}


// v4.8.0 : identité logique E2E d'un message. Le serveur peut retransmettre
// un même ciphertext ou, dans un ancien client, avoir créé plusieurs ids
// serveur ; un `mid` identique ne doit produire qu'une copie locale.
export function hasLogicalDuplicate(messages, from, mid) {
  const target = String(mid || '');
  if (!target) return false;
  return (Array.isArray(messages) ? messages : []).some((m) => m && m.from === from && String(m.mid || '') === target);
}

export function messagesMatchingRemoteDeletion(messages, from, targetId) {
  const target = String(targetId || '');
  if (!target) return [];
  return (Array.isArray(messages) ? messages : []).filter((m) => m && m.from === from
    && (String(m.mid || '') === target || String(m.serverId || '') === target));
}
